import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import { 
  SectionContentSkeleton, 
  QuizSkeleton, 
  SummarySkeleton, 
  LoadingWithProgress,
  BackgroundGenerationIndicator 
} from '@/components/SkeletonLoaders';
import SectionChat from '@/components/SectionChat';

const API = `${window.location.origin}/api`;

const LearningPage = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionContents, setSectionContents] = useState({});
  const [loadingSections, setLoadingSections] = useState({});
  const [sectionSummaries, setSectionSummaries] = useState({});
  const [loadingSummaries, setLoadingSummaries] = useState({});
  const [expandedSummaries, setExpandedSummaries] = useState({});
  const [sectionQuizzes, setSectionQuizzes] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResults, setShowQuizResults] = useState({});
  const [retakingQuiz, setRetakingQuiz] = useState({});
  const [retakeNumbers, setRetakeNumbers] = useState({}); // Track which retake number for each section
  const [loadingQuizzes, setLoadingQuizzes] = useState({}); // Track which quizzes are loading
  const [finalQuiz, setFinalQuiz] = useState(null);
  const [showFinalQuiz, setShowFinalQuiz] = useState(false);
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [finalQuizScore, setFinalQuizScore] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [completedSections, setCompletedSections] = useState({});
  // Essay challenge state
  const [essayPrompts, setEssayPrompts] = useState({});
  const [essayTexts, setEssayTexts] = useState({});
  const [essayFeedback, setEssayFeedback] = useState({});
  const [loadingEssayPrompt, setLoadingEssayPrompt] = useState({});
  const [submittingEssay, setSubmittingEssay] = useState({});
  const [showEssayChallenge, setShowEssayChallenge] = useState({});
  const touchStartX = React.useRef(0);
  const touchEndX = React.useRef(0);
  const sectionsContainerRef = React.useRef(null);
  const sectionRefsMap = React.useRef({});
  const finalQuizRef = React.useRef(null);

  // Scroll helper functions
  const scrollToSectionsContainer = () => {
    if (sectionsContainerRef.current) {
      // Calculate the target position BEFORE collapsing
      const yOffset = -100; // Offset to show some content above
      const y = sectionsContainerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      // Use instant scroll (no animation) to prevent jumping
      window.scrollTo({ top: y, behavior: 'instant' });
    }
  };

  const scrollToSectionQuiz = (sectionIndex) => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      const sectionRef = sectionRefsMap.current[sectionIndex];
      if (sectionRef) {
        const quizElement = sectionRef.querySelector('[data-quiz-container]');
        if (quizElement) {
          const yOffset = -20; // Small offset from top
          const y = quizElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }
    }, 100);
  };

  const scrollToFinalQuiz = () => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      if (finalQuizRef.current) {
        const yOffset = -20; // Small offset from top
        const y = finalQuizRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  const nextSectionGenerating = React.useRef(false); // Track if next section is already being generated
  const [backgroundGenSection, setBackgroundGenSection] = useState(null); // Track which section is generating in background
  const [loadingStages, setLoadingStages] = useState({}); // Track loading stages for each section
  const [loadingProgress, setLoadingProgress] = useState({}); // Track progress for each section
  const [expandedQuizzes, setExpandedQuizzes] = useState({}); // Track which quizzes are expanded

  useEffect(() => {
    // Scroll to top when entering the lesson page
    window.scrollTo(0, 0);
    
    // Load cached content from sessionStorage FIRST (synchronously)
    const cachedContent = sessionStorage.getItem(`section_content_${courseId}_${lessonId}`);
    const cachedSummaries = sessionStorage.getItem(`section_summaries_${courseId}_${lessonId}`);
    const cachedQuizzes = sessionStorage.getItem(`section_quizzes_${courseId}_${lessonId}`);
    const cachedQuizResults = sessionStorage.getItem(`section_quiz_results_${courseId}_${lessonId}`);
    const cachedCompletedSections = sessionStorage.getItem(`completed_sections_${courseId}_${lessonId}`);
    
    let initialContent = {};
    let initialSummaries = {};
    let initialQuizzes = {};
    let initialQuizResults = {};
    let initialCompletedSections = {};
    
    if (cachedContent) {
      try {
        initialContent = JSON.parse(cachedContent);
        setSectionContents(initialContent);
      } catch (e) { /* ignore parse errors */ }
    }
    
    if (cachedSummaries) {
      try {
        initialSummaries = JSON.parse(cachedSummaries);
        setSectionSummaries(initialSummaries);
      } catch (e) { /* ignore parse errors */ }
    }
    
    if (cachedQuizzes) {
      try {
        initialQuizzes = JSON.parse(cachedQuizzes);
        setSectionQuizzes(initialQuizzes);
      } catch (e) { /* ignore parse errors */ }
    }
    
    // Load cached quiz results (green checkmarks and scores)
    if (cachedQuizResults) {
      try {
        initialQuizResults = JSON.parse(cachedQuizResults);
        setShowQuizResults(initialQuizResults);
      } catch (e) { /* ignore parse errors */ }
    }
    
    // Load cached completed sections
    if (cachedCompletedSections) {
      try {
        initialCompletedSections = JSON.parse(cachedCompletedSections);
        setCompletedSections(initialCompletedSections);
      } catch (e) { /* ignore parse errors */ }
    }
    
    // Load lesson data with cache info
    loadLessonData(initialContent);
    
    // Reset final quiz states when lesson changes (section quiz results are preserved)
    setFinalQuiz(null);
    setShowFinalQuiz(false);
    setShowFinalResults(false);
    setFinalQuizScore(null);
    
    // Only reset quiz answers if we don't have cached results
    if (Object.keys(initialQuizResults).length === 0) {
      setQuizAnswers({});
    }
    
    setOpenSection(null);
    
    // Add swipe gesture listener
    const handleTouchStart = (e) => {
      touchStartX.current = e.changedTouches[0].screenX;
    };
    
    const handleTouchEnd = (e) => {
      touchEndX.current = e.changedTouches[0].screenX;
      handleSwipe();
    };
    
    const handleSwipe = () => {
      // Swipe left (start > end) with at least 100px distance
      if (touchStartX.current - touchEndX.current > 100) {
        navigate(`/course/${courseId}`);
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [courseId, lessonId, navigate]);

  const loadLessonData = async (cachedContent = {}) => {
    // Try to use cached course/lesson data first
    const cachedCourse = sessionStorage.getItem(`course_${courseId}`);
    const cachedProgress = sessionStorage.getItem(`course_progress_${courseId}`);
    
    let courseData = null;
    let progressData = null;
    
    // Use cached data if available for instant display
    if (cachedCourse && cachedProgress) {
      try {
        courseData = JSON.parse(cachedCourse);
        progressData = JSON.parse(cachedProgress);
        
        setCourse(courseData);
        setProgress(progressData);
        
        const currentLesson = courseData.lessons.find(
          l => l.lesson_number === parseInt(lessonId)
        );
        setLesson(currentLesson);
        setLoading(false);
        
        // If we have cached content, we're done - no need for API calls
        const hasCache = Object.keys(cachedContent).length > 0;
        if (hasCache) {
          console.log('📦 Using fully cached lesson data - skipping API calls');
          return;
        }
      } catch (e) {
        console.log('Cache parse error, falling back to API');
      }
    }
    
    // If no cache or need fresh data, fetch from API
    try {
      const [courseRes, progressRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`, { withCredentials: true }),
        axios.get(`${API}/progress/${courseId}`, { withCredentials: true })
      ]);

      // Cache the responses
      sessionStorage.setItem(`course_${courseId}`, JSON.stringify(courseRes.data));
      sessionStorage.setItem(`course_progress_${courseId}`, JSON.stringify(progressRes.data));

      setCourse(courseRes.data);
      setProgress(progressRes.data);

      const currentLesson = courseRes.data.lessons.find(
        l => l.lesson_number === parseInt(lessonId)
      );
      setLesson(currentLesson);
      
      // Only trigger section expand if we DON'T have cached content
      const hasCache = Object.keys(cachedContent).length > 0;
      if (currentLesson?.content_outline?.length > 0 && !hasCache) {
        // Trigger the cascade: content → summary → quiz → retake → next section
        setTimeout(() => {
          handleSectionExpand(0);
        }, 100);
      }
    } catch (error) {
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const loadSectionContent = async (sectionIndex) => {
    // Check if already loaded in state
    if (sectionContents[sectionIndex]) {
      console.log(`📦 Section ${sectionIndex} content already in state`);
      // Only trigger summary if not already loaded
      if (!sectionSummaries[sectionIndex]) {
        // Check sessionStorage for summary too
        const cachedSummaries = sessionStorage.getItem(`section_summaries_${courseId}_${lessonId}`);
        if (cachedSummaries) {
          try {
            const parsed = JSON.parse(cachedSummaries);
            if (parsed[sectionIndex]) {
              setSectionSummaries(prev => ({ ...prev, [sectionIndex]: parsed[sectionIndex] }));
              // Check quiz cache too
              const cachedQuizzes = sessionStorage.getItem(`section_quizzes_${courseId}_${lessonId}`);
              if (cachedQuizzes) {
                try {
                  const quizParsed = JSON.parse(cachedQuizzes);
                  if (quizParsed[sectionIndex]) {
                    setSectionQuizzes(prev => ({ ...prev, [sectionIndex]: quizParsed[sectionIndex] }));
                    return; // All cached, we're done!
                  }
                } catch (e) { /* ignore */ }
              }
              return;
            }
          } catch (e) { /* ignore */ }
        }
        loadSectionSummary(sectionIndex);
      }
      return;
    }
    
    // Double-check sessionStorage (state might not be updated yet)
    const cachedContent = sessionStorage.getItem(`section_content_${courseId}_${lessonId}`);
    if (cachedContent) {
      try {
        const parsed = JSON.parse(cachedContent);
        if (parsed[sectionIndex]) {
          console.log(`📦 Using cached content for section ${sectionIndex}`);
          setSectionContents(prev => ({ ...prev, [sectionIndex]: parsed[sectionIndex] }));
          
          // Also check and load cached summaries/quizzes
          const cachedSummaries = sessionStorage.getItem(`section_summaries_${courseId}_${lessonId}`);
          const cachedQuizzes = sessionStorage.getItem(`section_quizzes_${courseId}_${lessonId}`);
          
          if (cachedSummaries) {
            try {
              const summaryParsed = JSON.parse(cachedSummaries);
              if (summaryParsed[sectionIndex]) {
                setSectionSummaries(prev => ({ ...prev, [sectionIndex]: summaryParsed[sectionIndex] }));
              }
            } catch (e) { /* ignore */ }
          }
          
          if (cachedQuizzes) {
            try {
              const quizParsed = JSON.parse(cachedQuizzes);
              if (quizParsed[sectionIndex]) {
                setSectionQuizzes(prev => ({ ...prev, [sectionIndex]: quizParsed[sectionIndex] }));
                return; // All cached!
              }
            } catch (e) { /* ignore */ }
          }
          
          // If summary/quiz not cached, load them
          if (!sectionSummaries[sectionIndex]) {
            loadSectionSummary(sectionIndex);
          }
          return;
        }
      } catch (e) { /* ignore */ }
    }

    // No cache - load from API
    setLoadingSections(prev => ({ ...prev, [sectionIndex]: true }));
    
    // Progressive loading stages
    setLoadingStages(prev => ({ ...prev, [sectionIndex]: 'generating' }));
    setLoadingProgress(prev => ({ ...prev, [sectionIndex]: 10 }));
    
    try {
      // Simulate progressive stages for UX
      setTimeout(() => {
        setLoadingStages(prev => ({ ...prev, [sectionIndex]: 'analyzing' }));
        setLoadingProgress(prev => ({ ...prev, [sectionIndex]: 40 }));
      }, 1000);
      
      setTimeout(() => {
        setLoadingProgress(prev => ({ ...prev, [sectionIndex]: 70 }));
      }, 2000);
      
      const response = await axios.get(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/sections/${sectionIndex}/content`,
        { withCredentials: true }
      );
      
      setLoadingStages(prev => ({ ...prev, [sectionIndex]: 'finalizing' }));
      setLoadingProgress(prev => ({ ...prev, [sectionIndex]: 95 }));
      
      // Small delay for smooth transition
      setTimeout(() => {
        setSectionContents(prev => {
          const updated = { ...prev, [sectionIndex]: response.data.content };
          // Cache to sessionStorage
          sessionStorage.setItem(`section_content_${courseId}_${lessonId}`, JSON.stringify(updated));
          return updated;
        });
        setLoadingProgress(prev => ({ ...prev, [sectionIndex]: 100 }));
      }, 300);
      
      // After content loads, trigger summary generation (which will then trigger quiz)
      console.log(`📄 Content loaded for section ${sectionIndex}, now loading summary...`);
      setTimeout(() => {
        loadSectionSummary(sectionIndex);
      }, 500);
      
    } catch (error) {
      console.error('Failed to load section content:', error);
      // Silent fail - content will load on retry
    } finally {
      setTimeout(() => {
        setLoadingSections(prev => ({ ...prev, [sectionIndex]: false }));
        setLoadingStages(prev => ({ ...prev, [sectionIndex]: null }));
      }, 500);
    }
  };

  const loadSectionSummary = async (sectionIndex) => {
    // Don't reload if already loaded - but still trigger quiz chain
    if (sectionSummaries[sectionIndex]) {
      // Summary already loaded, trigger quiz if not loaded
      if (!sectionQuizzes[sectionIndex]) {
        loadSectionQuiz(sectionIndex);
      }
      return;
    }
    
    // Double-check sessionStorage
    const cachedSummaries = sessionStorage.getItem(`section_summaries_${courseId}_${lessonId}`);
    if (cachedSummaries) {
      try {
        const parsed = JSON.parse(cachedSummaries);
        if (parsed[sectionIndex]) {
          console.log(`📦 Using cached summary for section ${sectionIndex}`);
          setSectionSummaries(prev => ({ ...prev, [sectionIndex]: parsed[sectionIndex] }));
          if (!sectionQuizzes[sectionIndex]) {
            loadSectionQuiz(sectionIndex);
          }
          return;
        }
      } catch (e) { /* ignore */ }
    }

    setLoadingSummaries(prev => ({ ...prev, [sectionIndex]: true }));
    try {
      const response = await axios.get(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/sections/${sectionIndex}/summary`,
        { withCredentials: true }
      );
      setSectionSummaries(prev => {
        const updated = { ...prev, [sectionIndex]: response.data.summary };
        // Cache to sessionStorage
        sessionStorage.setItem(`section_summaries_${courseId}_${lessonId}`, JSON.stringify(updated));
        return updated;
      });
      
      // After summary loads, trigger quiz generation
      console.log(`📝 Summary loaded for section ${sectionIndex}, now loading quiz...`);
      setTimeout(() => {
        loadSectionQuiz(sectionIndex);
      }, 500);
      
    } catch (error) {
      toast.error('Failed to load summary');
      // Still try to load quiz even if summary fails
      setTimeout(() => {
        loadSectionQuiz(sectionIndex);
      }, 500);
    } finally {
      setLoadingSummaries(prev => ({ ...prev, [sectionIndex]: false }));
    }
  };

  const handleSectionChange = (value) => {
    // Only scroll if switching from one open section to another (not when opening from closed)
    const shouldScroll = openSection && value && openSection !== value;
    
    setOpenSection(value);
    
    if (shouldScroll) {
      // Use setTimeout to let the accordion state update first, then scroll instantly
      setTimeout(() => {
        const sectionsArea = document.querySelector('[data-testid="sections-accordion"]');
        if (sectionsArea) {
          sectionsArea.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }, 0);
    }
  };

  const handleSectionExpand = (sectionIndex) => {
    console.log(`🔍 Section ${sectionIndex} expanded, loading content...`);
    
    // Load content immediately - this will trigger the cascade:
    // content → summary → quiz → retake → next section
    loadSectionContent(sectionIndex);
    
    // Don't trigger next section here - wait for retake quiz to finish
  };

  const generateNextSectionInBackground = async () => {
    // Prevent duplicate generation calls
    if (nextSectionGenerating.current) {
      console.log('⏭️ Next section already generating, skipping duplicate call');
      return;
    }
    
    nextSectionGenerating.current = true;
    
    // Fire-and-forget call to generate the next ungenerated section
    try {
      console.log('🚀 Triggering next section generation in background...');
      const response = await axios.post(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/generate-next-section`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        console.log(`✅ Generated section ${response.data.section_index} in background`);
        // Show background generation indicator
        setBackgroundGenSection(response.data.section_index + 1); // +1 for user-friendly numbering
        setTimeout(() => setBackgroundGenSection(null), 5000);
        
        // Reset the flag after successful generation so next section can be generated later
        setTimeout(() => {
          nextSectionGenerating.current = false;
        }, 5000);
      } else {
        console.log(`ℹ️ ${response.data.message}`);
        nextSectionGenerating.current = false;
      }
    } catch (error) {
      console.error('❌ Background generation error:', error);
      nextSectionGenerating.current = false;
      // Fail silently - this is a background optimization, not critical
    }
  };

  const toggleSummary = (sectionIndex) => {
    setExpandedSummaries(prev => ({ ...prev, [sectionIndex]: !prev[sectionIndex] }));
  };

  const showSummaryView = (sectionIndex) => {
    return expandedSummaries[sectionIndex] === true;
  };

  const loadSectionQuiz = async (sectionIndex, retake = false) => {
    const currentRetakeNumber = retakeNumbers[sectionIndex] || 0;
    
    if (!retake && sectionQuizzes[sectionIndex]) return;
    
    // Double-check sessionStorage for non-retakes
    if (!retake) {
      const cachedQuizzes = sessionStorage.getItem(`section_quizzes_${courseId}_${lessonId}`);
      if (cachedQuizzes) {
        try {
          const parsed = JSON.parse(cachedQuizzes);
          if (parsed[sectionIndex]) {
            console.log(`📦 Using cached quiz for section ${sectionIndex}`);
            setSectionQuizzes(prev => ({ ...prev, [sectionIndex]: parsed[sectionIndex] }));
            return;
          }
        } catch (e) { /* ignore */ }
      }
    }

    // Set loading state
    setLoadingQuizzes(prev => ({ ...prev, [sectionIndex]: true }));

    try {
      const nextRetakeNumber = retake ? currentRetakeNumber + 1 : 0;
      console.log(`📖 Loading quiz for section ${sectionIndex}, retake number: ${nextRetakeNumber}, isRetake: ${retake}`);
      
      const response = await axios.get(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/sections/${sectionIndex}/quiz?retake_number=${nextRetakeNumber}`,
        { withCredentials: true }
      );
      
      console.log(`📝 Quiz response for section ${sectionIndex}:`, response.data.quiz ? `${response.data.quiz.questions.length} questions` : 'NO QUIZ');
      setSectionQuizzes(prev => {
        const updated = { ...prev, [sectionIndex]: response.data.quiz };
        // Cache to sessionStorage (only initial quiz, not retakes)
        if (!retake) {
          sessionStorage.setItem(`section_quizzes_${courseId}_${lessonId}`, JSON.stringify(updated));
        }
        return updated;
      });
      setRetakeNumbers(prev => ({ ...prev, [sectionIndex]: nextRetakeNumber }));
      setLoadingQuizzes(prev => ({ ...prev, [sectionIndex]: false }));
      
      // After loading quiz (whether initial or retake), trigger retake pre-generation
      // This ensures: Quiz loads → Retake generates → Next section generates
      if (retake) {
        // Clear answers for retake
        setQuizAnswers(prev => {
          const newAnswers = { ...prev };
          Object.keys(newAnswers).forEach(key => {
            if (key.startsWith(`${sectionIndex}-`)) {
              delete newAnswers[key];
            }
          });
          return newAnswers;
        });
        // Hide the result box to show fresh quiz
        setShowQuizResults(prev => {
          const newResults = { ...prev };
          delete newResults[sectionIndex];
          return newResults;
        });
        // Also clear from sessionStorage
        try {
          const storageKey = `section_quiz_results_${courseId}_${lessonId}`;
          const existing = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
          delete existing[sectionIndex];
          sessionStorage.setItem(storageKey, JSON.stringify(existing));
          
          const completedKey = `completed_sections_${courseId}_${lessonId}`;
          const existingCompleted = JSON.parse(sessionStorage.getItem(completedKey) || '{}');
          delete existingCompleted[sectionIndex];
          sessionStorage.setItem(completedKey, JSON.stringify(existingCompleted));
        } catch (e) { /* ignore */ }
        
        setCompletedSections(prev => {
          const newCompleted = { ...prev };
          delete newCompleted[sectionIndex];
          return newCompleted;
        });
        setRetakingQuiz(prev => {
          const newRetaking = { ...prev };
          delete newRetaking[sectionIndex];
          return newRetaking;
        });
      }
      
      // ALWAYS trigger retake pre-generation after quiz loads (initial or retake)
      // This creates the chain: Quiz → Retake → Next Section
      console.log(`📚 Quiz loaded for section ${sectionIndex}, starting retake pre-generation...`);
      setTimeout(() => pregenerateNextRetake(sectionIndex), 500);
    } catch (error) {
      console.error(`❌ Failed to load quiz for section ${sectionIndex}:`, error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to load quiz. Please try again.');
      // Set empty state so the fallback UI shows
      setSectionQuizzes(prev => ({ ...prev, [sectionIndex]: null }));
      setLoadingQuizzes(prev => ({ ...prev, [sectionIndex]: false }));
    }
  };

  // Pre-generate the next retake quiz in the background
  const pregenerateNextRetake = async (sectionIndex) => {
    try {
      console.log(`🎯 Starting retake quiz pre-generation for section ${sectionIndex}...`);
      await axios.post(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/sections/${sectionIndex}/pregenerate-retake`,
        {},
        { withCredentials: true }
      );
      console.log(`✅ Retake quiz pre-generated for section ${sectionIndex}`);
      
      // Wait a moment to ensure quiz is fully saved before starting next section
      // This prevents competing for LLM resources
      console.log(`🚀 Retake quiz done, waiting 2s before starting next section generation...`);
      setTimeout(() => {
        generateNextSectionInBackground();
      }, 2000);
    } catch (error) {
      console.log('⚠️ Pre-generation failed, will generate on demand');
      // Still try to generate next section even if retake failed
      generateNextSectionInBackground();
    }
  };

  const loadFinalQuiz = async (retake = false) => {
    try {
      const response = await axios.get(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/final-quiz?retake=${retake}`,
        { withCredentials: true }
      );
      setFinalQuiz(response.data.quiz);
      
      // Clear previous answers if retaking (but keep old score until new one is submitted)
      if (retake) {
        setQuizAnswers({});
        setShowFinalResults(false);
        // Final quiz loaded silently
      }
    } catch (error) {
      toast.error('Failed to load final quiz');
    }
  };

  const handleQuizAnswer = (sectionIndex, questionIndex, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [`${sectionIndex}-${questionIndex}`]: answer
    }));
  };

  const submitSectionQuiz = async (sectionIndex) => {
    const quiz = sectionQuizzes[sectionIndex];
    let correct = 0;
    quiz.questions.forEach((q, i) => {
      const userAnswer = quizAnswers[`${sectionIndex}-${i}`];
      // Normalize both answers by removing any parentheses and trimming
      const normalizedUserAnswer = userAnswer?.trim().replace(/[()]/g, '');
      const normalizedCorrectAnswer = q.correct_answer?.trim().replace(/[()]/g, '');
      
      if (normalizedUserAnswer === normalizedCorrectAnswer) {
        correct++;
      }
    });
    
    const quizResult = { score: correct, total: quiz.questions.length };
    
    setShowQuizResults(prev => ({
      ...prev,
      [sectionIndex]: quizResult
    }));
    
    // Clear retaking flag
    setRetakingQuiz(prev => {
      const newRetaking = { ...prev };
      delete newRetaking[sectionIndex];
      return newRetaking;
    });
    
    // Mark section as completed
    setCompletedSections(prev => ({
      ...prev,
      [sectionIndex]: true
    }));
    
    // Save section quiz results to sessionStorage for persistence
    const storageKey = `section_quiz_results_${courseId}_${lessonId}`;
    try {
      const existing = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
      existing[sectionIndex] = quizResult;
      sessionStorage.setItem(storageKey, JSON.stringify(existing));
    } catch (e) { /* ignore */ }
    
    // Save completed sections to sessionStorage
    const completedKey = `completed_sections_${courseId}_${lessonId}`;
    try {
      const existingCompleted = JSON.parse(sessionStorage.getItem(completedKey) || '{}');
      existingCompleted[sectionIndex] = true;
      sessionStorage.setItem(completedKey, JSON.stringify(existingCompleted));
    } catch (e) { /* ignore */ }
    
    toast.success(`You scored ${correct}/${quiz.questions.length}!`);
    
    // Don't trigger generation here - it's triggered when quiz loads, not when submitted
  };

  // Essay Challenge Functions
  const loadEssayPrompt = async (sectionIndex) => {
    if (essayPrompts[sectionIndex] || loadingEssayPrompt[sectionIndex]) return;
    
    setLoadingEssayPrompt(prev => ({ ...prev, [sectionIndex]: true }));
    try {
      const sectionTitle = lesson?.sections?.[sectionIndex]?.title || 
                          lesson?.content_outline?.[sectionIndex] || 
                          `Section ${sectionIndex + 1}`;
      const content = sectionContents[sectionIndex] || '';
      
      const response = await axios.post(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/sections/${sectionIndex}/essay-prompt`,
        { 
          section_title: typeof sectionTitle === 'string' ? sectionTitle : `Section ${sectionIndex + 1}`,
          section_content: content 
        },
        { withCredentials: true }
      );
      
      setEssayPrompts(prev => ({ ...prev, [sectionIndex]: response.data.prompt }));
    } catch (error) {
      toast.error('Failed to load essay challenge');
    } finally {
      setLoadingEssayPrompt(prev => ({ ...prev, [sectionIndex]: false }));
    }
  };

  const submitEssay = async (sectionIndex) => {
    const essayText = essayTexts[sectionIndex];
    const wordCount = essayText?.split(/\s+/).filter(w => w).length || 0;
    if (!essayText || wordCount < 10) {
      toast.error('Please write at least 10 words');
      return;
    }
    
    setSubmittingEssay(prev => ({ ...prev, [sectionIndex]: true }));
    try {
      const sectionTitle = lesson?.sections?.[sectionIndex]?.title || 
                          lesson?.content_outline?.[sectionIndex] || 
                          `Section ${sectionIndex + 1}`;
      const content = sectionContents[sectionIndex] || '';
      const prompt = essayPrompts[sectionIndex] || '';
      
      const response = await axios.post(
        `${API}/courses/${courseId}/lessons/${parseInt(lessonId)}/sections/${sectionIndex}/essay-critique`,
        { 
          section_title: typeof sectionTitle === 'string' ? sectionTitle : `Section ${sectionIndex + 1}`,
          section_content: content,
          essay_prompt: prompt,
          essay_text: essayText
        },
        { withCredentials: true }
      );
      
      setEssayFeedback(prev => ({ ...prev, [sectionIndex]: response.data.feedback }));
      toast.success('Essay submitted! Check your feedback below.');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit essay';
      toast.error(msg);
    } finally {
      setSubmittingEssay(prev => ({ ...prev, [sectionIndex]: false }));
    }
  };

  const toggleEssayChallenge = (sectionIndex) => {
    setShowEssayChallenge(prev => {
      const newState = { ...prev, [sectionIndex]: !prev[sectionIndex] };
      if (newState[sectionIndex] && !essayPrompts[sectionIndex]) {
        loadEssayPrompt(sectionIndex);
      }
      return newState;
    });
  };

  const handleFinalQuizAnswer = (questionIndex, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [`final-${questionIndex}`]: answer
    }));
  };

  const submitFinalQuiz = async () => {
    let correct = 0;
    finalQuiz.questions.forEach((q, i) => {
      const userAnswer = quizAnswers[`final-${i}`];
      // Normalize both answers by removing any parentheses and trimming
      const normalizedUserAnswer = userAnswer?.trim().replace(/[()]/g, '');
      const normalizedCorrectAnswer = q.correct_answer?.trim().replace(/[()]/g, '');
      
      if (normalizedUserAnswer === normalizedCorrectAnswer) {
        correct++;
      }
    });
    
    const score = (correct / finalQuiz.questions.length) * 100;
    setFinalQuizScore({ correct, total: finalQuiz.questions.length, percentage: Math.round(score) });
    setShowFinalResults(true);
    
    toast.success(`Final Quiz Score: ${correct}/${finalQuiz.questions.length} (${Math.round(score)}%)`);
    
    // Save the quiz score to progress
    try {
      await axios.put(
        `${API}/progress/${courseId}/quiz-score`,
        { lesson_id: parseInt(lessonId), score: Math.round(score) },
        { withCredentials: true }
      );
    } catch (error) {
      // Silently fail - score saving is not critical for learning
    }
    
    // Mark lesson as complete when final quiz is taken (regardless of score)
    await markLessonComplete(false); // Don't navigate away
  };

  const markLessonComplete = async (shouldNavigate = true) => {
    try {
      const completedLessons = progress?.completed_lessons || [];
      const currentLessonNum = parseInt(lessonId);

      if (!completedLessons.includes(currentLessonNum)) {
        completedLessons.push(currentLessonNum);
      }

      const totalLessons = course.lessons.length;
      const completionPercentage = (completedLessons.length / totalLessons) * 100;

      await axios.put(
        `${API}/progress/${courseId}`,
        {
          current_lesson: currentLessonNum + 1,
          completed_lessons: completedLessons,
          completion_percentage: completionPercentage
        },
        { withCredentials: true }
      );

      // Update local progress state so checkmark shows immediately
      setProgress(prev => ({
        ...prev,
        completed_lessons: completedLessons,
        completion_percentage: completionPercentage
      }));

      if (shouldNavigate) {
        toast.success('Lesson completed!');

        // Navigate to next lesson or back to course
        const nextLesson = course.lessons.find(l => l.lesson_number === currentLessonNum + 1);
        if (nextLesson) {
          navigate(`/learn/${courseId}/${nextLesson.lesson_number}`);
        } else {
          toast.success('Congratulations! You completed the course!');
          navigate(`/course/${courseId}`);
        }
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const goToNextLesson = () => {
    const currentLessonNum = parseInt(lessonId);
    const nextLesson = course.lessons.find(l => l.lesson_number === currentLessonNum + 1);
    if (nextLesson) {
      navigate(`/learn/${courseId}/${nextLesson.lesson_number}`);
    }
  };

  const goToPreviousLesson = () => {
    const currentLessonNum = parseInt(lessonId);
    const prevLesson = course.lessons.find(l => l.lesson_number === currentLessonNum - 1);
    if (prevLesson) {
      navigate(`/learn/${courseId}/${prevLesson.lesson_number}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Card className="card-glow p-8 text-center">
          <h2 className="text-2xl font-bold text-amber-900 mb-4">Lesson not found</h2>
          <Button onClick={() => navigate(`/course/${courseId}`)} className="bg-amber-600 hover:bg-amber-700">
            Back to Course
          </Button>
        </Card>
      </div>
    );
  }

  const currentLessonNum = parseInt(lessonId);
  const hasNextLesson = course.lessons.some(l => l.lesson_number === currentLessonNum + 1);
  const hasPrevLesson = course.lessons.some(l => l.lesson_number === currentLessonNum - 1);
  const isCompleted = progress?.completed_lessons?.includes(currentLessonNum);

  return (
    <div className="min-h-screen theme-bg">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b sticky top-0 z-50 shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/course/${courseId}`)}
              className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
              data-testid="back-to-course-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Course
            </Button>
            <div className="flex items-center space-x-4">
              {/* Segmented Progress Bar */}
              <div className="flex items-center gap-1">
                {course.lessons.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 w-6 rounded-sm transition-colors ${
                      idx + 1 < currentLessonNum
                        ? 'bg-green-500'
                        : idx + 1 === currentLessonNum
                        ? 'bg-amber-500'
                        : isDarkMode
                        ? 'bg-gray-600'
                        : 'bg-gray-300'
                    }`}
                    title={`Lesson ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Lesson Content */}
        <Card className="card-glow border-amber-200 shadow-xl mb-8" data-testid="lesson-content">
          <CardContent className="p-8">
            {/* Lesson Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-2 mb-4">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Lesson {lesson.lesson_number}</span>
                {isCompleted && (
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-testid="lesson-completed-badge" />
                )}
              </div>
              <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>{lesson.title}</h1>
              <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>Duration: {lesson.duration_minutes} minutes</p>
            </div>

            {/* Learning Objectives */}
            <div className="mb-8 p-6 bg-amber-50 rounded-xl border border-amber-200">
              <h2 className="text-xl font-bold text-amber-900 mb-4">Learning Objectives</h2>
              <ul className="space-y-2">
                {lesson.objectives?.map((obj, i) => (
                  <li key={i} className="flex items-start text-amber-800">
                    <span className="mr-3 text-amber-600 font-bold">✓</span>
                    <span>{obj}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Content Sections */}
            <div className="mb-8" ref={sectionsContainerRef}>
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Lesson Content</h2>
              <p className={`mb-6 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Click on each section to expand and read the detailed content</p>
              <Accordion 
                type="single" 
                collapsible 
                className="space-y-4"
                value={openSection}
                onValueChange={handleSectionChange}
                data-testid="sections-accordion"
              >
                {lesson.content_outline?.map((point, i) => (
                  <AccordionItem
                    key={i}
                    value={`section-${i}`}
                    className="border border-amber-200 rounded-lg overflow-hidden bg-white shadow-sm"
                    data-testid={`section-${i}`}
                    ref={(el) => { if (el) sectionRefsMap.current[i] = el; }}
                  >
                    <AccordionTrigger 
                      className="px-6 py-4 hover:bg-amber-50 text-left"
                      onClick={() => handleSectionExpand(i)}
                    >
                      <div className="flex items-start space-x-3 w-full">
                        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                          {completedSections[i] ? (
                            <CheckCircle2 className="w-6 h-6 text-green-600" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-semibold">
                              <span>{i + 1}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-amber-900 text-lg">{point}</h3>
                        </div>
                        {showQuizResults[i] && (
                          <div className={`flex-shrink-0 px-3 py-1 rounded-full text-sm font-semibold ${
                            showQuizResults[i].score === showQuizResults[i].total
                              ? 'bg-green-100 text-green-700 border border-green-300'
                              : showQuizResults[i].score >= showQuizResults[i].total * 0.6
                              ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                              : 'bg-red-100 text-red-700 border border-red-300'
                          }`}>
                            Quiz: {showQuizResults[i].score}/{showQuizResults[i].total}
                          </div>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 bg-amber-50/30">
                      {loadingSections[i] ? (
                        <div className="space-y-6">
                          <LoadingWithProgress 
                            stage={loadingStages[i] || 'loading'}
                            progress={loadingProgress[i] || 0}
                            showProgress={true}
                          />
                          <SectionContentSkeleton />
                        </div>
                      ) : sectionContents[i] ? (
                        <div className="space-y-6">
                          {/* Summary Toggle Button */}
                          {sectionSummaries[i] && !loadingSummaries[i] && (
                            <div className="flex justify-end">
                              <button
                                onClick={() => toggleSummary(i)}
                                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-lg shadow-md transition-all transform hover:scale-105"
                                data-testid={`toggle-summary-${i}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                </svg>
                                <span className="font-medium">{showSummaryView(i) ? 'Show Full Content' : 'View AI Summary'}</span>
                              </button>
                            </div>
                          )}

                          {loadingSummaries[i] && (
                            <div className="space-y-4">
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3">
                                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                <span className="text-blue-700 text-sm font-medium">Generating AI summary...</span>
                              </div>
                              <SummarySkeleton />
                            </div>
                          )}

                          {/* Show AI Summary if toggled ON */}
                          {showSummaryView(i) && sectionSummaries[i] && (
                            <div 
                              className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6 cursor-pointer hover:border-blue-300 transition-colors"
                              onClick={() => toggleSummary(i)}
                            >
                              <div className="flex items-center space-x-2 mb-3">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-blue-600">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                </svg>
                                <h4 className="font-bold text-blue-900">AI Condensed Summary</h4>
                                <span className="text-blue-500 text-sm ml-auto">(click to show full content)</span>
                              </div>
                              <div className="text-blue-900 leading-relaxed">
                                <ReactMarkdown
                                  components={{
                                    h1: ({node, ...props}) => <h1 className="text-xl font-bold text-blue-900 mt-4 mb-3" {...props} />,
                                    h2: ({node, ...props}) => <h2 className="text-lg font-bold text-blue-900 mt-3 mb-2" {...props} />,
                                    h3: ({node, ...props}) => <h3 className="text-base font-bold text-blue-900 mt-3 mb-2" {...props} />,
                                    h4: ({node, ...props}) => <h4 className="text-base font-semibold text-blue-900 mt-2 mb-1" {...props} />,
                                    p: ({node, ...props}) => <p className="text-base leading-relaxed mb-3 text-blue-900" {...props} />,
                                    ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-1 mb-3 ml-4" {...props} />,
                                    ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-1 mb-3 ml-4" {...props} />,
                                    li: ({node, ...props}) => <li className="text-base leading-relaxed text-blue-900" {...props} />,
                                    strong: ({node, ...props}) => <strong className="font-bold text-blue-900" {...props} />,
                                    em: ({node, ...props}) => <em className="italic" {...props} />,
                                  }}
                                >
                                  {sectionSummaries[i]}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}

                          {/* Full Content - Show by default (when summary is NOT toggled) */}
                          {!showSummaryView(i) && (
                            <div 
                              className="max-w-none animate-fade-in text-amber-800 cursor-pointer"
                              onClick={() => {
                                scrollToSectionsContainer();
                                setOpenSection(null);
                              }}
                            >
                              <ReactMarkdown
                                components={{
                                  h1: ({node, ...props}) => <div className="text-base font-bold text-amber-900 mt-4 mb-2" {...props} />,
                                  h2: ({node, ...props}) => <div className="text-base font-bold text-amber-900 mt-4 mb-2" {...props} />,
                                  h3: ({node, ...props}) => <div className="text-base font-bold text-amber-900 mt-4 mb-2" {...props} />,
                                  h4: ({node, ...props}) => <div className="text-base font-bold text-amber-900 mt-4 mb-2" {...props} />,
                                  p: ({node, ...props}) => <p className="text-base leading-relaxed mb-4 text-amber-800" {...props} />,
                                  ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-4 ml-4" {...props} />,
                                  ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-4 ml-4" {...props} />,
                                  li: ({node, ...props}) => <li className="text-base leading-relaxed text-amber-800" {...props} />,
                                  strong: ({node, ...props}) => <strong className="font-bold text-amber-900" {...props} />,
                                  em: ({node, ...props}) => <em className="italic" {...props} />,
                                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-amber-400 pl-4 py-2 my-4 italic text-amber-700 bg-amber-50" {...props} />,
                                  code: ({node, inline, ...props}) => 
                                    inline 
                                      ? <code className="bg-amber-100 px-1.5 py-0.5 rounded text-sm font-mono text-amber-900" {...props} />
                                      : <code className="block bg-amber-100 p-3 rounded text-sm font-mono text-amber-900 overflow-x-auto my-3" {...props} />,
                                  a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-800 underline" {...props} />,
                                }}
                              >
                                {sectionContents[i]}
                              </ReactMarkdown>
                            </div>
                          )}
                          
                          {/* Section Quiz */}
                          {loadingQuizzes[i] ? (
                            <div className="mt-6 p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
                              <div className="flex flex-col items-center justify-center space-y-3">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                <p className="text-blue-800 text-center font-medium">
                                  Generating quiz for this section...
                                </p>
                                <p className="text-blue-600 text-sm text-center">
                                  This may take 1-2 minutes. Please wait...
                                </p>
                              </div>
                            </div>
                          ) : sectionQuizzes[i] ? (
                            <div className="mt-6 bg-white rounded-lg border-2 border-blue-200 animate-fade-in" data-quiz-container>
                              <button
                                onClick={() => setExpandedQuizzes(prev => ({ ...prev, [i]: !prev[i] }))}
                                className="w-full px-6 py-4 flex items-center justify-between hover:bg-blue-50 transition-colors"
                              >
                                <h4 className="text-lg font-bold text-blue-900">Section Quiz - Test Your Understanding</h4>
                                <svg 
                                  className={`w-5 h-5 text-blue-900 transition-transform ${expandedQuizzes[i] ? 'rotate-180' : ''}`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              
                              {expandedQuizzes[i] && (
                              <div className="px-6 pb-6 space-y-4">
                                {sectionQuizzes[i].questions.map((question, qIdx) => (
                                  <div key={qIdx} className="space-y-2" data-testid={`section-${i}-question-${qIdx}`}>
                                    <p className="font-semibold text-amber-900">{qIdx + 1}. {question.question}</p>
                                    <div className="space-y-2 ml-4">
                                      {question.choices.map((choice, cIdx) => {
                                        const letter = choice.charAt(0);
                                        const isSelected = quizAnswers[`${i}-${qIdx}`] === letter;
                                        // Normalize correct answer by removing parentheses
                                        const normalizedCorrectAnswer = question.correct_answer?.trim().replace(/[()]/g, '');
                                        const isCorrect = showQuizResults[i] && letter === normalizedCorrectAnswer;
                                        const showResult = showQuizResults[i];
                                        
                                        return (
                                          <label
                                            key={cIdx}
                                            className={`flex items-start p-2 rounded cursor-pointer ${
                                              showResult
                                                ? isCorrect
                                                  ? 'bg-green-100 border border-green-400'
                                                  : isSelected
                                                  ? 'bg-red-100 border border-red-400'
                                                  : 'bg-gray-50'
                                                : isSelected
                                                ? 'bg-blue-100 border border-blue-400'
                                                : 'hover:bg-amber-50'
                                            }`}
                                          >
                                            <input
                                              type="radio"
                                              name={`quiz-${i}-${qIdx}`}
                                              value={letter}
                                              checked={isSelected}
                                              onChange={() => handleQuizAnswer(i, qIdx, letter)}
                                              disabled={showQuizResults[i]}
                                              className="mt-1 mr-2"
                                            />
                                            <span className={`text-sm ${isDarkMode ? 'text-gray-900' : ''}`}>{choice}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                    {showQuizResults[i] && (
                                      <p className="text-sm text-gray-700 ml-4 mt-2 italic">
                                        {question.explanation}
                                      </p>
                                    )}
                                  </div>
                                ))}
                                {!showQuizResults[i] ? (
                                  <div className="flex justify-center mt-4">
                                    <Button
                                      onClick={() => submitSectionQuiz(i)}
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                      data-testid={`submit-section-quiz-${i}`}
                                    >
                                      Submit Quiz
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="mt-4 space-y-3">
                                    <div className="p-4 bg-green-50 border border-green-200 rounded">
                                      <p className="text-green-800 font-semibold">
                                        Score: {showQuizResults[i].score}/{showQuizResults[i].total}
                                      </p>
                                    </div>
                                    <Button
                                      onClick={() => {
                                        // Instantly scroll to quiz section BEFORE anything else
                                        const sectionRef = sectionRefsMap.current[i];
                                        if (sectionRef) {
                                          const quizElement = sectionRef.querySelector('[data-quiz-container]');
                                          if (quizElement) {
                                            quizElement.scrollIntoView({ behavior: 'instant', block: 'start' });
                                          }
                                        }
                                        loadSectionQuiz(i, true);
                                      }}
                                      variant="outline"
                                      className="w-full border-amber-600 text-amber-600 hover:bg-amber-50"
                                    >
                                      Retake Quiz
                                    </Button>
                                  </div>
                                )}
                              </div>
                              )}
                            </div>
                          ) : (
                            <div className="mt-6 p-6 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                              <p className="text-yellow-800 text-center">
                                ⚠️ Quiz not loaded for this section. Please refresh the page or contact support.
                              </p>
                              <Button
                                onClick={() => loadSectionQuiz(i, false)}
                                className="mt-3 mx-auto block bg-yellow-600 hover:bg-yellow-700"
                              >
                                Load Quiz
                              </Button>
                            </div>
                          )}
                          
                          {/* Essay Challenge - Optional */}
                          {sectionContents[i] && (
                            <div className="mt-6">
                              <button
                                onClick={() => toggleEssayChallenge(i)}
                                className={`w-full p-4 rounded-lg border-2 border-dashed transition-all ${
                                  showEssayChallenge[i]
                                    ? 'border-purple-400 bg-purple-50'
                                    : 'border-gray-300 hover:border-purple-300 hover:bg-purple-50/50'
                                }`}
                              >
                                <div className="flex items-center justify-center gap-2">
                                  <span className="text-2xl">✍️</span>
                                  <span className={`font-semibold ${showEssayChallenge[i] ? 'text-purple-800' : 'text-gray-700'}`}>
                                    {showEssayChallenge[i] ? 'Essay Challenge' : 'Try the Essay Challenge (Optional)'}
                                  </span>
                                </div>
                                {!showEssayChallenge[i] && (
                                  <p className="text-sm text-gray-500 mt-1">
                                    Write a short essay and get AI feedback on your arguments
                                  </p>
                                )}
                              </button>
                              
                              {showEssayChallenge[i] && (
                                <div className="mt-4 p-5 bg-purple-50 rounded-lg border border-purple-200 animate-fade-in">
                                  {loadingEssayPrompt[i] ? (
                                    <div className="flex items-center justify-center py-6">
                                      <Loader2 className="w-6 h-6 animate-spin text-purple-600 mr-2" />
                                      <span className="text-purple-700">Generating your challenge...</span>
                                    </div>
                                  ) : essayPrompts[i] ? (
                                    <div className="space-y-4">
                                      {/* Essay Prompt */}
                                      <div className="p-4 bg-white rounded-lg border border-purple-200">
                                        <p className="text-purple-900 font-medium italic">
                                          "{essayPrompts[i]}"
                                        </p>
                                      </div>
                                      
                                      {/* Essay Input */}
                                      {!essayFeedback[i] ? (
                                        <>
                                          <textarea
                                            value={essayTexts[i] || ''}
                                            onChange={(e) => setEssayTexts(prev => ({ ...prev, [i]: e.target.value }))}
                                            placeholder="Write your 200-word essay here..."
                                            className="w-full h-48 p-4 rounded-lg border border-purple-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 outline-none resize-none"
                                            disabled={submittingEssay[i]}
                                          />
                                          <div className="flex items-center justify-between">
                                            <span className={`text-sm ${
                                              (essayTexts[i]?.split(/\s+/).filter(w => w).length || 0) >= 180
                                                ? 'text-green-600'
                                                : (essayTexts[i]?.split(/\s+/).filter(w => w).length || 0) >= 100
                                                ? 'text-yellow-600'
                                                : 'text-gray-500'
                                            }`}>
                                              {essayTexts[i]?.split(/\s+/).filter(w => w).length || 0} / 200 words
                                            </span>
                                            <Button
                                              onClick={() => submitEssay(i)}
                                              disabled={submittingEssay[i] || (essayTexts[i]?.split(/\s+/).filter(w => w).length || 0) < 10}
                                              className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                              {submittingEssay[i] ? (
                                                <>
                                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                  Getting Feedback...
                                                </>
                                              ) : (
                                                'Submit for Critique'
                                              )}
                                            </Button>
                                          </div>
                                        </>
                                      ) : (
                                        /* Feedback Display */
                                        <div className="space-y-4">
                                          <div className="p-4 bg-white rounded-lg border border-gray-200">
                                            <p className="text-sm text-gray-600 mb-2 font-medium">Your Essay:</p>
                                            <p className="text-gray-800 text-sm whitespace-pre-wrap">{essayTexts[i]}</p>
                                          </div>
                                          <div className="p-4 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg border border-purple-200">
                                            <p className="text-sm text-purple-700 mb-2 font-semibold">Professor Clio's Feedback:</p>
                                            <p className="text-gray-800 whitespace-pre-wrap">{essayFeedback[i]}</p>
                                          </div>
                                          <Button
                                            onClick={() => {
                                              setEssayTexts(prev => ({ ...prev, [i]: '' }));
                                              setEssayFeedback(prev => ({ ...prev, [i]: null }));
                                            }}
                                            variant="outline"
                                            className="border-purple-300 text-purple-700 hover:bg-purple-100"
                                          >
                                            Try Again
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-center text-gray-500 py-4">
                                      Failed to load essay prompt. Please try again.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Collapse Section Button */}
                          <div className="mt-6 flex justify-center">
                            <Button
                              onClick={() => {
                                // Scroll first, then collapse to prevent page jump
                                if (sectionsContainerRef.current) {
                                  const yOffset = -100;
                                  const y = sectionsContainerRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                  window.scrollTo({ top: y, behavior: 'instant' });
                                }
                                // Use requestAnimationFrame to ensure scroll completes first
                                requestAnimationFrame(() => {
                                  setOpenSection(null);
                                });
                              }}
                              variant="outline"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              data-testid={`collapse-section-${i}`}
                            >
                              <ChevronUp className="w-4 h-4 mr-2" />
                              Collapse Section
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-amber-600 italic">Click to load content...</p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Key Terms */}
            <div className="mb-8 p-6 bg-orange-50 rounded-xl border border-orange-200">
              <h2 className="text-xl font-bold text-orange-900 mb-4">Key Terms</h2>
              <div className="flex flex-wrap gap-3">
                {lesson.key_terms?.map((term, i) => (
                  <span key={i} className="px-4 py-2 bg-white border border-orange-300 rounded-full text-orange-800 font-medium">
                    {term}
                  </span>
                ))}
              </div>
            </div>

            {/* Final Lesson Quiz */}
            {!showFinalQuiz ? (
              <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-300">
                <h2 className="text-2xl font-bold text-purple-900 mb-4">Ready for the Final Quiz?</h2>
                <p className="text-purple-800 mb-4">Test your knowledge of the entire lesson with 10 questions.</p>
                <Button
                  onClick={() => { setShowFinalQuiz(true); loadFinalQuiz(); }}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="start-final-quiz-btn"
                >
                  Start Final Quiz
                </Button>
              </div>
            ) : finalQuiz ? (
              <div className="mt-8 p-6 bg-white rounded-xl border-2 border-purple-300" ref={finalQuizRef}>
                <h2 className="text-2xl font-bold text-purple-900 mb-6">Final Lesson Quiz</h2>
                {finalQuizScore && (
                  <div className="mb-6 space-y-3">
                    <div className={`p-4 rounded-lg border-2 ${
                      finalQuizScore.percentage >= 70 ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'
                    }`}>
                      <p className={`text-lg font-bold ${
                        finalQuizScore.percentage >= 70 ? 'text-green-900' : 'text-yellow-900'
                      }`}>
                        Final Score: {finalQuizScore.correct}/{finalQuizScore.total} ({finalQuizScore.percentage}%)
                      </p>
                      {finalQuizScore.percentage >= 70 ? (
                        <p className="text-green-800 mt-1">Great job! Lesson completed.</p>
                      ) : (
                        <p className="text-yellow-800 mt-1">You can review the material and retake the quiz.</p>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        loadFinalQuiz(true);
                        scrollToFinalQuiz();
                      }}
                      variant="outline"
                      className="w-full border-amber-600 text-amber-600 hover:bg-amber-50"
                    >
                      Retake Final Quiz
                    </Button>
                  </div>
                )}
                <div className="space-y-6">
                  {finalQuiz.questions.map((question, qIdx) => (
                    <div key={qIdx} className="space-y-2" data-testid={`final-question-${qIdx}`}>
                      <p className="font-semibold text-amber-900">{qIdx + 1}. {question.question}</p>
                      <div className="space-y-2 ml-4">
                        {question.choices.map((choice, cIdx) => {
                          const letter = choice.charAt(0);
                          const isSelected = quizAnswers[`final-${qIdx}`] === letter;
                          // Normalize correct answer by removing parentheses
                          const normalizedCorrectAnswer = question.correct_answer?.trim().replace(/[()]/g, '');
                          const isCorrect = showFinalResults && letter === normalizedCorrectAnswer;
                          
                          return (
                            <label
                              key={cIdx}
                              className={`flex items-start p-2 rounded cursor-pointer ${
                                showFinalResults
                                  ? isCorrect
                                    ? 'bg-green-100 border border-green-400'
                                    : isSelected
                                    ? 'bg-red-100 border border-red-400'
                                    : 'bg-gray-50'
                                  : isSelected
                                  ? 'bg-purple-100 border border-purple-400'
                                  : 'hover:bg-purple-50'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`final-quiz-${qIdx}`}
                                value={letter}
                                checked={isSelected}
                                onChange={() => handleFinalQuizAnswer(qIdx, letter)}
                                disabled={showFinalResults}
                                className="mt-1 mr-2"
                              />
                              <span className={`text-sm ${isDarkMode ? 'text-gray-900' : ''}`}>{choice}</span>
                            </label>
                          );
                        })}
                      </div>
                      {showFinalResults && (
                        <p className="text-sm text-gray-700 ml-4 mt-2 italic">
                          {question.explanation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {!showFinalResults ? (
                  <div className="flex justify-center mt-6">
                    <Button
                      onClick={submitFinalQuiz}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="submit-final-quiz-btn"
                    >
                      Submit Final Quiz
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => {
                      setShowFinalResults(false);
                      setQuizAnswers({});
                      setFinalQuizScore(null);
                      scrollToFinalQuiz();
                    }}
                    className="mt-6 bg-purple-600 hover:bg-purple-700"
                    data-testid="retake-final-quiz-btn"
                  >
                    Retake Quiz
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                <span className="ml-3 text-purple-700">Loading final quiz...</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between items-center py-6 border-t border-amber-200/50">
          <button
            onClick={goToPreviousLesson}
            disabled={!hasPrevLesson}
            className={`flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
              !hasPrevLesson 
                ? 'opacity-30 cursor-not-allowed' 
                : isDarkMode 
                  ? 'text-amber-300 hover:text-amber-100' 
                  : 'text-amber-700 hover:text-amber-900'
            }`}
            data-testid="previous-lesson-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          {hasNextLesson ? (
            <button
              onClick={goToNextLesson}
              className={`flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
                isDarkMode 
                  ? 'text-amber-300 hover:text-amber-100' 
                  : 'text-amber-700 hover:text-amber-900'
              }`}
              data-testid="next-lesson-btn"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => navigate(`/course/${courseId}`)}
              className={`flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
                isDarkMode 
                  ? 'text-amber-300 hover:text-amber-100' 
                  : 'text-amber-700 hover:text-amber-900'
              }`}
              data-testid="back-to-course-complete-btn"
            >
              <span>Back to Course</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </main>
      
      {/* Background Generation Indicator */}
      <BackgroundGenerationIndicator 
        sectionNumber={backgroundGenSection} 
        isGenerating={backgroundGenSection !== null} 
      />
      
      {/* Section Chat - Fixed bottom right */}
      {openSection && (() => {
        const sectionIndex = parseInt(openSection.replace('section-', ''));
        const section = lesson?.sections?.[sectionIndex] || lesson?.content_outline?.[sectionIndex];
        const sectionTitle = section?.title || section || `Section ${sectionIndex + 1}`;
        return (
          <SectionChat
            sectionTitle={typeof sectionTitle === 'string' ? sectionTitle : `Section ${sectionIndex + 1}`}
            sectionContent={sectionContents[sectionIndex] || ''}
            courseTitle={course?.title || ''}
            lessonTitle={lesson?.title || ''}
          />
        );
      })()}
    </div>
  );
};

export default LearningPage;